import CalendarIcon from '@mui/icons-material/CalendarMonth';
import JobsIcon from '@mui/icons-material/Work';
import StaffIcon from '@mui/icons-material/People';
import StaffGroupsIcon from '@mui/icons-material/Badge';
import SkillsetIcon from '@mui/icons-material/ConstructionOutlined';
import TimesheetIcon from '@mui/icons-material/Schedule';
import ClientsIcon from '@mui/icons-material/ContactPage';
import InvoicingIcon from '@mui/icons-material/ReceiptLong';
import SmsIcon from '@mui/icons-material/Sms';
import NotesIcon from '@mui/icons-material/StickyNote2';
import CompanyIcon from '@mui/icons-material/Business';
import WorkCategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Tune';
import SwmsIcon from '@mui/icons-material/HealthAndSafety';
import type { ReactNode } from 'react';
import { ACCENTS } from '../lib/theme';

/**
 * The modules. Single source of truth for the dashboard tiles and the routes.
 *
 * This replaces the legacy `menu` database table, which is not migrated. That
 * table stored raw HTML in its `icon` column and echoed it unescaped into the
 * page; more importantly it let navigation drift away from the code — it still
 * listed modules whose controllers had been deleted, and had an enabled
 * "Calendar Duplicates" row pointing at a controller that was never written.
 *
 * Declaring modules here means a tile cannot exist without a route, and adding
 * one is a single entry. Permissions stay data: `slug` is matched against
 * role_permissions in Supabase, and the API enforces it per endpoint with
 * requirePermission(), so hiding a tile is presentation only.
 */

export interface ModuleEntry {
    /** Permission slug — the same string stored in role_permissions. */
    readonly slug: string;
    readonly label: string;
    readonly description: string;
    readonly icon: ReactNode;
    readonly colour: string;
    readonly group: string;
    readonly path: string;
    /** False renders the tile dimmed and unclickable. Omit for available modules. */
    readonly available?: boolean;
}

/** Fixed section order. Sections with no visible tiles are dropped at render time. */
export const GROUP_ORDER: readonly string[] = [
    'Scheduling',
    'People',
    'Clients & Billing',
    'Communication',
    'Settings',
];

export const MODULES: readonly ModuleEntry[] = [
    // ─── Scheduling ─────────────────────────────────────────────────────────
    {
        slug: 'calendar',
        label: 'Calendar',
        description: 'Roster staff onto jobs, day by day',
        icon: <CalendarIcon fontSize="large" />,
        colour: ACCENTS.blue,
        group: 'Scheduling',
        path: '/calendar',
    },
    {
        slug: 'jobs',
        label: 'Jobs',
        description: 'Job details, SWMS and project files',
        icon: <JobsIcon fontSize="large" />,
        colour: ACCENTS.amber,
        group: 'Scheduling',
        path: '/jobs',
    },

    // ─── People ─────────────────────────────────────────────────────────────
    {
        slug: 'timesheet',
        label: 'Timesheet',
        description: 'Hours, overtime, allowances and leave',
        icon: <TimesheetIcon fontSize="large" />,
        colour: ACCENTS.purple,
        group: 'People',
        path: '/timesheet',
    },
    {
        slug: 'staff',
        label: 'Staff',
        description: 'Staff records and employment details',
        icon: <StaffIcon fontSize="large" />,
        colour: ACCENTS.green,
        group: 'People',
        path: '/staff',
    },
    {
        slug: 'staff-groups',
        label: 'Staff Groups',
        description: 'Roles and what each one can access',
        icon: <StaffGroupsIcon fontSize="large" />,
        colour: ACCENTS.green,
        group: 'People',
        path: '/staff-groups',
    },
    {
        slug: 'skillsets',
        label: 'Skillsets',
        description: 'Skillsets, hire rates and calendar colours',
        icon: <SkillsetIcon fontSize="large" />,
        colour: ACCENTS.teal,
        group: 'People',
        path: '/skillsets',
    },

    // ─── Clients & billing ──────────────────────────────────────────────────
    {
        slug: 'clients',
        label: 'Clients',
        description: 'Client records, contacts and rates',
        icon: <ClientsIcon fontSize="large" />,
        colour: ACCENTS.indigo,
        group: 'Clients & Billing',
        path: '/clients',
    },
    {
        slug: 'outstanding-invoicing',
        label: 'Outstanding Invoicing',
        description: 'Work completed and not yet invoiced',
        icon: <InvoicingIcon fontSize="large" />,
        colour: ACCENTS.pink,
        group: 'Clients & Billing',
        path: '/outstanding-invoicing',
    },

    // ─── Communication ──────────────────────────────────────────────────────
    {
        slug: 'sms',
        label: 'SMS / Job Notes',
        description: 'Send SMS to staff and log replies',
        icon: <SmsIcon fontSize="large" />,
        colour: ACCENTS.blue,
        group: 'Communication',
        path: '/sms',
    },
    {
        slug: 'notes',
        label: 'Notes',
        description: 'Job notes shared with crews',
        icon: <NotesIcon fontSize="large" />,
        colour: ACCENTS.amber,
        group: 'Communication',
        path: '/notes',
    },

    // ─── Settings ───────────────────────────────────────────────────────────
    {
        slug: 'company-details',
        label: 'Company Details',
        description: 'Company, ABN and payroll period',
        icon: <CompanyIcon fontSize="large" />,
        colour: ACCENTS.teal,
        group: 'Settings',
        path: '/company-details',
    },
    {
        slug: 'work-category',
        label: 'Work Category',
        description: 'Work categories and leave types',
        icon: <WorkCategoryIcon fontSize="large" />,
        colour: ACCENTS.purple,
        group: 'Settings',
        path: '/work-category',
    },
    {
        slug: 'swms',
        label: 'SWMS',
        description: 'SWMS task library and risk rankings',
        icon: <SwmsIcon fontSize="large" />,
        colour: ACCENTS.red,
        group: 'Settings',
        path: '/swms',
    },
    {
        slug: 'program-settings',
        label: 'Program Settings',
        description: 'Job file types, staff types and calendar types',
        icon: <SettingsIcon fontSize="large" />,
        colour: ACCENTS.indigo,
        group: 'Settings',
        path: '/program-settings',
    },
];

/** Every permission slug the app knows about. Used to seed role_permissions. */
export const ALL_SLUGS: readonly string[] = MODULES.map((m) => m.slug);

/** Modules grouped for the dashboard, in GROUP_ORDER, empty groups dropped. */
export function groupModules(modules: readonly ModuleEntry[]): { heading: string; items: ModuleEntry[] }[] {
    return GROUP_ORDER.map((heading) => ({
        heading,
        items: modules.filter((m) => m.group === heading),
    })).filter((g) => g.items.length > 0);
}
